import { Box, Drawer, Typography, Divider, IconButton, FormHelperText, Paper, Switch, useTheme } from '@mui/material';
import { IoClose, IoAdd } from 'react-icons/io5';
import { MdDragIndicator, MdDeleteOutline } from 'react-icons/md';
import { useForm, Controller, useFieldArray } from 'react-hook-form';
import { useEffect, useMemo, useState } from 'react';
import Input from '@/components/Common/Input';
import Select from '@/components/Common/Select';
import Button from '@/components/Common/Button';
import { ColumnDataType, SelectOption } from '@/apis/business/agent/worksheet/types';
import { WORKSHEET_SELECT_COLORS } from './worksheetSelectColors';

export interface ColumnFormData {
  data_type: ColumnDataType;
  name: string;
  description: string;
  required: boolean;
  options: SelectOption[];
}

interface ColumnDrawerProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: ColumnFormData) => void;
  initialData?: Partial<ColumnFormData>;
  mode?: 'add' | 'edit';
  isPending?: boolean;
  textOnly?: boolean;
}

const COLUMN_TYPE_OPTIONS: { value: ColumnDataType; label: string }[] = [
  { value: 'text', label: 'Text' },
  { value: 'number', label: 'Number' },
  { value: 'single_select', label: 'Single Select' },
  { value: 'datetime', label: 'Date & Time' }
];

const createEmptyOption = (): SelectOption => ({
  id: crypto.randomUUID(),
  label: '',
  color: 'green'
});

export default function ColumnDrawer({
  open,
  onClose,
  onSubmit,
  initialData,
  mode = 'add',
  isPending = false,
  textOnly = false
}: ColumnDrawerProps) {
  const theme = useTheme();
  const {
    control,
    handleSubmit,
    watch,
    reset,
    formState: { errors }
  } = useForm<ColumnFormData>({
    defaultValues: {
      data_type: 'text',
      name: '',
      description: '',
      required: false,
      options: [createEmptyOption(), createEmptyOption()]
    }
  });

  const { fields, append, remove, move } = useFieldArray({
    control,
    name: 'options',
    keyName: 'fieldKey'
  });

  const dataType = watch('data_type');
  const [dragIndex, setDragIndex] = useState<number | null>(null);

  const colorOptions = useMemo(
    () =>
      WORKSHEET_SELECT_COLORS.map((color) => ({
        value: color.key,
        label: color.label
      })),
    []
  );

  useEffect(() => {
    if (!open) return;

    reset({
      data_type: initialData?.data_type || 'text',
      name: initialData?.name || '',
      description: initialData?.description || '',
      required: initialData?.required || false,
      options:
        initialData?.options && initialData.options.length > 0
          ? initialData.options
          : [createEmptyOption(), createEmptyOption()]
    });
  }, [open, initialData, reset]);

  const handleFormSubmit = (data: ColumnFormData) => {
    if (data.data_type === 'single_select') {
      const validOptions = data.options.filter((option) => option.label.trim() && option.color);
      if (validOptions.length === 0) return;
      onSubmit({ ...data, options: validOptions, description: '', required: false });
      return;
    }

    if (data.data_type !== 'text') {
      onSubmit({ ...data, options: [], description: '', required: false });
      return;
    }

    onSubmit({ ...data, options: [] });
  };

  const handleDragStart = (index: number) => {
    setDragIndex(index);
  };

  const handleDragOver = (event: React.DragEvent, index: number) => {
    event.preventDefault();
    if (dragIndex === null || dragIndex === index) return;
    move(dragIndex, index);
    setDragIndex(index);
  };

  const handleDragEnd = () => {
    setDragIndex(null);
  };

  return (
    <Drawer
      anchor='right'
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: 480,
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          borderRadius: '4px 0 0 4px',
          boxShadow: '0 0 20px rgba(0,0,0,0.08)',
          overflow: 'hidden'
        }
      }}
    >
      <Box
        px={3}
        py={2.5}
        display='flex'
        alignItems='center'
        justifyContent='space-between'
        borderBottom='1px solid #F0F0F0'
        flexShrink={0}
      >
        <Typography fontSize={20} fontWeight={600}>
          {mode === 'add' ? 'Add Column' : 'Edit Column'}
        </Typography>
        <IconButton onClick={onClose} size='small'>
          <IoClose size={22} />
        </IconButton>
      </Box>

      <Box
        component='form'
        onSubmit={(e) => {
          e.stopPropagation();
          handleSubmit(handleFormSubmit)(e);
        }}
        display='flex'
        flexDirection='column'
        flex={1}
        minHeight={0}
      >
        <Box px={3} py={3} flex={1} overflow='auto' display='flex' flexDirection='column' gap={3}>
          <Select
            name='data_type'
            control={control}
            label='Type'
            fullWidth
            disabled={mode === 'edit' || textOnly}
            sortOrder='none'
            rules={{ required: true }}
            options={COLUMN_TYPE_OPTIONS}
          />

          <Input
            name='name'
            control={control}
            label='Name'
            placeholder='Enter Name Here'
            fullWidth
            required
            rules={{ required: 'Name is required' }}
          />

          {dataType === 'text' && (
            <>
              <Box>
                <Input
                  name='description'
                  control={control}
                  label='Description'
                  placeholder='Enter Here'
                  fullWidth
                  multiline
                  minRows={3}
                  helperText='Describe what this attribute represents, the value it should contain, and the expected format for the value. This description will be used when the worksheet is attached to an agent action and the Al agent needs to collect and write a value for this attribute during the conversation.'
                />
              </Box>

              <Paper
                variant='outlined'
                sx={{
                  bgcolor: '#F8F9FA',
                  borderColor: '#F8F9FA',
                  borderRadius: '8px',
                  p: 1.5,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 2
                }}
              >
                <Box>
                  <Typography fontWeight={500}>Required Field</Typography>
                  <Typography fontSize={12} color='text.secondary' lineHeight={1.3}>
                    When enabled, the Al agent must collect this attribute's value before marking the agent action as
                    completed or writing the data to the worksheet.
                  </Typography>
                </Box>

                <Controller
                  name='required'
                  control={control}
                  render={({ field }) => (
                    <Switch checked={field.value} onChange={(e) => field.onChange(e.target.checked)} />
                  )}
                />
              </Paper>
            </>
          )}

          {dataType === 'single_select' && (
            <Box>
              <Typography fontSize={14} fontWeight={500} mb={1.5}>
                Options
              </Typography>

              {fields.map((field, index) => (
                <Box
                  key={field.fieldKey}
                  draggable
                  onDragStart={() => handleDragStart(index)}
                  onDragOver={(event) => handleDragOver(event, index)}
                  onDragEnd={handleDragEnd}
                  display='flex'
                  alignItems='flex-start'
                  gap={1}
                  mb={1.5}
                  sx={{ opacity: dragIndex === index ? 0.6 : 1 }}
                >
                  <Box pt={2.5} sx={{ cursor: 'grab', color: '#9e9e9e' }}>
                    <MdDragIndicator size={20} />
                  </Box>

                  <Box flex={1} minWidth={0}>
                    <Select
                      name={`options.${index}.color`}
                      control={control}
                      label='Color'
                      fullWidth
                      required
                      sortOrder='none'
                      rules={{ required: 'Color is required' }}
                      options={colorOptions}
                    />
                  </Box>

                  <Box flex={1.4} minWidth={0}>
                    <Input
                      name={`options.${index}.label`}
                      control={control}
                      label='Option Label'
                      placeholder='Enter Here'
                      fullWidth
                      required
                      rules={{ required: 'Option label is required' }}
                    />
                  </Box>

                  <Box pt={1.5} display='flex' alignItems='center' gap={0.5}>
                    <IconButton
                      size='small'
                      onClick={() => {
                        if (fields.length > 1) remove(index);
                      }}
                      disabled={fields.length <= 1}
                      sx={{ color: theme.palette.error.main }}
                    >
                      <MdDeleteOutline size={20} />
                    </IconButton>
                    {index === fields.length - 1 && (
                      <IconButton
                        size='small'
                        onClick={() => append(createEmptyOption())}
                        sx={{ color: theme.palette.primary.main }}
                      >
                        <IoAdd size={20} />
                      </IconButton>
                    )}
                  </Box>
                </Box>
              ))}

              {errors.options && <FormHelperText error>Add at least one option with color and label</FormHelperText>}
            </Box>
          )}
        </Box>

        <Divider />
        <Box px={3} py={2} display='flex' justifyContent='flex-end' alignItems='center' gap={1.5} flexShrink={0}>
          <Button
            variant='outlined'
            onClick={onClose}
            sx={{
              textTransform: 'none',
              borderRadius: '999px',
              px: 3,
              color: '#000',
              borderColor: '#d9d9d9'
            }}
          >
            Cancel
          </Button>
          <Button type='submit' variant='contained' isPending={isPending} sx={{ px: 3 }}>
            {mode === 'add' ? 'Create' : 'Save'}
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
}
